import * as Sentry from '@sentry/react';
import { reactRouterV6Instrumentation } from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import {
  initConsoleCapture,
  destroyConsoleCapture,
  flushConsoleLogs,
  type ConsoleCaptureConfig,
} from './console-capture';

// Console 日志捕获配置
const DEFAULT_CONSOLE_CAPTURE_CONFIG: Partial<ConsoleCaptureConfig> = {
  enabled: true,
  levels: ['log', 'info', 'warning', 'error'],
  bufferSize: 100,
  autoFlushInterval: 60000, // 1分钟自动上传
  maxMessageLength: 2000,
  preserveOriginal: true,
  // 过滤开发环境噪音
  excludePrefixes: [
    '[HMR]',
    '[vite]',
    '[webpack]',
    'Download the React DevTools',
  ],
  // 自定义过滤函数
  filter: (level, args) => {
    // 过滤掉一些高频但无意义的日志
    const firstArg = args[0];
    if (
      typeof firstArg === 'string' &&
      firstArg.includes('[Performance]') &&
      level === 'debug'
    ) {
      return false;
    }
    return true;
  },
};

function createSentry() {
  let client: Sentry.BrowserClient | undefined;
  let consoleCaptureEnabled = false;

  const wrapped = {
    init(consoleCaptureConfig?: Partial<ConsoleCaptureConfig>) {
      if (BUILD_CONFIG.SENTRY_DSN && !client) {
        // https://docs.sentry.io/platforms/javascript/guides/react/#configure
        client = Sentry.init({
          dsn: BUILD_CONFIG.SENTRY_DSN,
          debug: BUILD_CONFIG.debug ?? false,
          environment: BUILD_CONFIG.appBuildType,
          integrations: defaults => [
            ...defaults,
            Sentry.browserTracingIntegration({}),
            Sentry.browserProfilingIntegration(),
            // Sentry.replayIntegration({
            //   maskAllText: false,
            //   blockAllMedia: false,
            //   networkDetailAllowUrls: [
            //     "https://api.muset.ai",
            //     "https://api-va.muset.ai",
            //     "wss://api-va.muset.ai",
            //   ],
            // }),
          ],
          // replaysSessionSampleRate: 0,
          // 性能追踪采样率
          tracesSampleRate: 0.1, // 生产环境 10%
          // 性能分析采样率
          profilesSampleRate: 0.1, // 生产环境 10%
          sendDefaultPii: true,
          // 增加 breadcrumbs 最大数量，以便记录更多日志
          maxBreadcrumbs: 200,
          // 控制哪些 URL 启用追踪
          tracePropagationTargets: [
            'localhost',
            /^https:\/\/.*\.wenxiaobai\.com/,
            /^https:\/\/.*\.muset\.ai/,
          ],
          // 在发送事件前的钩子，可以添加额外信息
          beforeSend(event, hint) {
            // 确保所有事件都带有 console logs 上下文
            return event;
          },
        }) as Sentry.BrowserClient;

        Sentry.setTags({
          distribution: BUILD_CONFIG.distribution,
          appVersion: BUILD_CONFIG.appVersion,
          editorVersion: BUILD_CONFIG.editorVersion,
        });

        // 初始化 Console 日志捕获
        const mergedConfig = {
          ...DEFAULT_CONSOLE_CAPTURE_CONFIG,
          ...consoleCaptureConfig,
        };
        initConsoleCapture(mergedConfig);
        consoleCaptureEnabled = true;
      }
    },

    enable() {
      if (client) {
        client.getOptions().enabled = true;
      }
    },

    disable() {
      if (client) {
        client.getOptions().enabled = false;
      }
    },

    /**
     * 手动上传 Console 日志
     * 可以在关键操作前后调用，确保日志被及时上传
     */
    flushConsoleLogs() {
      if (consoleCaptureEnabled) {
        flushConsoleLogs();
      }
    },

    /**
     * 销毁 Console 日志捕获
     */
    destroyConsoleCapture() {
      if (consoleCaptureEnabled) {
        destroyConsoleCapture();
        consoleCaptureEnabled = false;
      }
    },
  };

  return wrapped;
}

export const sentry = createSentry();
